import { useCallback, useEffect, useState } from "react";

import { useDropzone } from "react-dropzone";

import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  Upload,
  X,
} from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useAccountMappings } from "~/hooks/contabilidad/useAccountMappings";
import { useAccountsByCompany } from "~/hooks/contabilidad/useAccounts";
import { downloadMappingTemplate } from "~/lib/contabilidad/generateMappingTemplate";
import {
  type MappingParseResult,
  hasImportableMappings,
  hasMappingValidationErrors,
  parseMappingFile,
} from "~/lib/contabilidad/parseMappingFile";
import { cn } from "~/lib/utils";
import { accountMappingService } from "~/services/contabilidad/accountMappingService";
import { CLASE_TYPE_COLORS, REPORTE_TYPE_COLORS } from "~/types/contabilidad";

interface MapeoImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateName: string;
  companyId: string;
  onSuccess?: () => void;
}

export function MapeoImportDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
  companyId,
  onSuccess,
}: MapeoImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<MappingParseResult | null>(
    null
  );
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    updated: number;
    errors: Array<{ account_code: string; error: string }>;
  } | null>(null);

  const { data: accounts = [] } = useAccountsByCompany(companyId);
  const { data: existingMappings = [] } = useAccountMappings({
    template_id: templateId,
  });

  // Create sets for quick lookup
  const existingAccountCodes = new Set(
    accounts.map((acc) => acc.code.toLowerCase())
  );
  const existingMappingAccountCodes = new Set(
    existingMappings.map((m) => m.account?.code.toLowerCase() || "")
  );

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setFile(null);
      setParseResult(null);
      setImportResult(null);
    }
  }, [open]);

  const handleParseFile = async (uploadedFile: File) => {
    setIsParsing(true);
    try {
      const result = await parseMappingFile(
        uploadedFile,
        existingAccountCodes,
        existingMappingAccountCodes
      );
      setParseResult(result);
    } catch (error) {
      console.error("Error parsing file:", error);
      setParseResult({
        lines: [],
        errors: ["Error al procesar el archivo"],
        summary: { toCreate: 0, toUpdate: 0, invalid: 0 },
      });
    } finally {
      setIsParsing(false);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const uploadedFile = acceptedFiles[0];
      if (uploadedFile) {
        setFile(uploadedFile);
        setImportResult(null);
        handleParseFile(uploadedFile);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [existingAccountCodes, existingMappingAccountCodes]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  const handleRemoveFile = () => {
    setFile(null);
    setParseResult(null);
    setImportResult(null);
  };

  const handleSubmit = async () => {
    if (!parseResult || !hasImportableMappings(parseResult)) {
      return;
    }

    setIsImporting(true);
    try {
      // Get valid lines (no errors)
      const validLines = parseResult.lines.filter((line) => !line.error);

      const result = await accountMappingService.importMappings(
        templateId,
        companyId,
        validLines.map((line) => ({
          account_code: line.account_code,
          reporte: line.reporte,
          clase: line.clase,
          orden_clase: line.orden_clase,
          orden_tipo: line.orden_tipo,
          tipo: line.tipo,
          sub_clase: line.sub_clase,
          orden_subclase: line.orden_subclase,
          sub_clase_2: line.sub_clase_2,
          orden_subclase_2: line.orden_subclase_2,
          grupo_cuenta_code: line.grupo_cuenta_code,
          orden_grupo_cuenta: line.orden_grupo_cuenta,
          clave: line.clave,
          signo: line.signo,
          clase_1: line.clase_1,
        }))
      );

      setImportResult(result);

      if (result.errors.length === 0) {
        onSuccess?.();
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Error importing mappings:", error);
      setImportResult({
        created: 0,
        updated: 0,
        errors: [
          {
            account_code: "",
            error:
              error instanceof Error
                ? error.message
                : "Error desconocido al importar",
          },
        ],
      });
    } finally {
      setIsImporting(false);
    }
  };

  const canSubmit =
    parseResult &&
    !hasMappingValidationErrors(parseResult) &&
    hasImportableMappings(parseResult);

  const totalImportable =
    (parseResult?.summary.toCreate ?? 0) + (parseResult?.summary.toUpdate ?? 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[1000px]">
        <DialogHeader>
          <DialogTitle>Importar Mapeos</DialogTitle>
          <DialogDescription>
            Cargue un archivo Excel con los mapeos de cuentas para la plantilla
            "{templateName}".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Download & File Upload */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Archivo de Datos</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => downloadMappingTemplate(templateName)}
                className="h-8"
              >
                <Download className="mr-2 h-4 w-4" />
                Descargar Plantilla
              </Button>
            </div>

            {!file ? (
              <div
                {...getRootProps()}
                className={cn(
                  "cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors",
                  isDragActive
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/25 hover:border-primary/50"
                )}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">
                  {isDragActive
                    ? "Suelte el archivo aquí..."
                    : "Arrastre un archivo aquí o haga clic para seleccionar"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Formatos permitidos: .xlsx, .xls, .csv
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-xl border bg-muted/50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleRemoveFile}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Parsing indicator */}
          {isParsing && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm text-muted-foreground">
                Procesando archivo...
              </span>
            </div>
          )}

          {/* Global Errors */}
          {parseResult && parseResult.errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-inside list-disc">
                  {parseResult.errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Import Result */}
          {importResult && importResult.errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium">Errores durante la importación:</p>
                <ul className="mt-2 list-inside list-disc">
                  {importResult.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx}>
                      {err.account_code
                        ? `${err.account_code}: ${err.error}`
                        : err.error}
                    </li>
                  ))}
                  {importResult.errors.length > 5 && (
                    <li>...y {importResult.errors.length - 5} errores más</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview Table */}
          {parseResult && parseResult.lines.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Vista Previa ({parseResult.lines.length} filas)</Label>
                <div className="flex gap-3 text-sm">
                  {parseResult.summary.toCreate > 0 && (
                    <span className="text-green-600">
                      {parseResult.summary.toCreate} a crear
                    </span>
                  )}
                  {parseResult.summary.toUpdate > 0 && (
                    <span className="text-blue-600">
                      {parseResult.summary.toUpdate} a actualizar
                    </span>
                  )}
                  {parseResult.summary.invalid > 0 && (
                    <span className="text-destructive">
                      {parseResult.summary.invalid} inválidas
                    </span>
                  )}
                </div>
              </div>
              <div className="max-h-[300px] overflow-auto rounded-xl border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">Fila</TableHead>
                      <TableHead className="w-[100px]">Codigo</TableHead>
                      <TableHead className="w-[100px]">Reporte</TableHead>
                      <TableHead className="w-[120px]">Clase</TableHead>
                      <TableHead className="w-[120px]">Sub Clase</TableHead>
                      <TableHead className="w-[130px]">SubClase 2</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parseResult.lines.map((line, idx) => (
                      <TableRow
                        key={idx}
                        className={cn(
                          "duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both",
                          line.error && "bg-destructive/10",
                          line.existsInDb && !line.error && "bg-blue-50"
                        )}
                        style={{
                          animationDelay: `${Math.min(idx * 40, 400)}ms`,
                        }}
                      >
                        <TableCell className="text-muted-foreground">
                          {line.rowNumber}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {line.account_code}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs",
                              REPORTE_TYPE_COLORS[line.reporte]
                            )}
                          >
                            {line.reporte}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs",
                              CLASE_TYPE_COLORS[line.clase]
                            )}
                          >
                            {line.clase}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[120px] truncate text-sm">
                          {line.sub_clase}
                        </TableCell>
                        <TableCell className="max-w-[130px] truncate text-sm">
                          {line.sub_clase_2}
                        </TableCell>
                        <TableCell>
                          {line.error && (
                            <AlertCircle
                              className="h-4 w-4 text-destructive"
                              title={line.error}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={7}>
                        <div className="flex gap-4 text-sm">
                          {parseResult.summary.toCreate > 0 && (
                            <span className="text-green-600">
                              {parseResult.summary.toCreate} a crear
                            </span>
                          )}
                          {parseResult.summary.toUpdate > 0 && (
                            <span className="text-blue-600">
                              {parseResult.summary.toUpdate} a actualizar
                            </span>
                          )}
                          {parseResult.summary.invalid > 0 && (
                            <span className="text-destructive">
                              {parseResult.summary.invalid} con errores
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>

              {/* Line errors */}
              {parseResult.lines.some((l) => l.error) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-inside list-disc">
                      {parseResult.lines
                        .filter((l) => l.error)
                        .slice(0, 5)
                        .map((line, idx) => (
                          <li key={idx}>
                            Fila {line.rowNumber}: {line.error}
                          </li>
                        ))}
                      {parseResult.lines.filter((l) => l.error).length > 5 && (
                        <li>
                          ...y{" "}
                          {parseResult.lines.filter((l) => l.error).length - 5}{" "}
                          errores más
                        </li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={isImporting || !canSubmit}
              onClick={handleSubmit}
            >
              {isImporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importando...
                </>
              ) : (
                `Importar ${totalImportable} Mapeos`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
